/**
 * Utility to dynamically load third-party scripts into the DOM
 */

export interface ScriptLoadOptions {
  async?: boolean;
  defer?: boolean;
  type?: string;
}

/**
 * Loads a single script into the document body
 */
export const loadScript = (src: string, options: ScriptLoadOptions = {}): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if script already exists
    // eslint-disable-next-line i18next/no-literal-string
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }

    // eslint-disable-next-line i18next/no-literal-string
    const script = document.createElement('script');
    script.src = src;

    if (options.async) {
      script.async = true;
    }
    if (options.defer) {
      script.defer = true;
    }
    if (options.type) {
      script.type = options.type;
    }

    script.addEventListener('load', () => resolve());
    // eslint-disable-next-line i18next/no-literal-string
    script.addEventListener('error', () => reject(new Error(`Failed to load script: ${src}`)));

    document.body.append(script);
  });
};

/**
 * Loads multiple scripts sequentially
 */
export const loadScripts = async (scripts: string[], options: ScriptLoadOptions = {}): Promise<void> => {
  for (const src of scripts) {
    await loadScript(src, options);
  }
};

/**
 * Loads all configured third-party scripts
 */
export const loadThirdPartyScripts = async (options?: ScriptLoadOptions): Promise<void> => {
  const { getThirdPartyScripts } = await import('./thirdPartyScripts');
  const scripts = getThirdPartyScripts();
  const finalOptions = options || { async: true };
  await loadScripts(scripts, finalOptions);
};
